import PropTypes from 'prop-types';

import { Box, styled, Skeleton, Typography } from '@mui/joy';
import { HexColorPicker } from "react-colorful";
import { useState } from 'react';
import { useEffect } from 'react';
import { ExclamationMark, Lock } from '@phosphor-icons/react';
import { Toggle } from './Toggle';

const InputFieldLabel = styled(Box, {
  name: 'InputField', // The component name
  slot: 'label', // The slot name
})(({ theme }) => ({
  backgroundColor: theme.vars.palette.background.surface,
  borderWidth: theme.variants.outlined.borderWidth,
  borderStyle: 'solid',
  boxShadow: theme.vars.shadow.md,
  alignSelf: 'flex-start',
  fontSize: '16px',
  padding: '8px 12px',
  borderBottomWidth: 0,
  borderColor: theme.variants.outlined.neutral.borderColor,
  fontWeight: 600,
  transform: 'translateY(1px)'
}));

export const InputFieldFrame = styled('div', {
  name: 'InputField',
  slot: 'frame'
})(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  gap: 1,
  justifyContent: 'stretch',
  borderRadius: 0,
  padding: 0,
  boxShadow: 'none',
  maxHeight: 200,
  borderStyle: 'solid',
  borderColor: theme.variants.outlined.neutral.borderColor,
  backgroundColor: theme.vars.palette.background.surface,
  borderWidth: theme.variants.outlined.borderWidth,
  
  /*button: {
    color: theme.vars.palette.primary.solidBg + ' !important'
  }*/
}));

export const InputFieldRoot = styled(Box, {
  name: 'InputField',
  slot: 'root'
})(({ theme }) => ({
  '&:focus-within': {
    div: {
      borderColor: 'CanvasText'
    },
    
  },
  /*'&:has(:invalid)': {
    color: theme.vars.palette.danger[500],
    div: {
      borderColor: theme.vars.palette.danger[500]
    }
  },*/
  '&.error': {
    color: theme.vars.palette.danger[500],
    div: {
      borderColor: theme.vars.palette.danger[500]
    }
  },
}))

const InputFieldText = styled(
  "input", 
  {
    name: 'InputField',
    slot: 'value',
    shouldForwardProp: (props) => props !== "focusColor"
  }
)(() => ({
  border: 'none',
  flex: 1,
  padding: '8px 12px',
  fontSize: '16px',
  fontFamily: "DM Mono",
  fontWeight: 'light',
  '&::placeholder': {
    fontFamily: "DM Mono",
  },
  '&:focus': {
    outline: '2pt solid CanvasText'
  }
}))

export function InputField({
  label,
  element,
  loading = false,
  readOnly = false,
  info,
  errors=[],
  children
}) {
  return (
    <InputFieldRoot className={errors?.length > 0 ? 'error' : ''} sx={{ display: 'flex', marginBottom: 1, flexDirection: 'column', alignItems: 'stretch' }}>
      <InputFieldLabel>
        {label}
        {readOnly && (
          <Lock />
        )}
      </InputFieldLabel>
      <Box sx={{ display: 'flex', flexDirection: 'row', transform: 'translateY(-1pt)', position: 'relative', minHeight: '30pt'}}>
        {loading ?
          <Skeleton loading={loading} />: <Box sx={{ flex: 1 }}>{element}{children}</Box>
        }
      </Box>
      {errors?.map(error => (
        <Typography key={error.description} startDecorator={<ExclamationMark />} color="danger">{error.description}</Typography>
      ))}
      {info &&
        <Typography sx={{ opacity: 0.5 }}>{info}</Typography>
      }
    </InputFieldRoot>
  )
}

InputField.propTypes = {
  label: PropTypes.string,
  element: PropTypes.node,
  loading: PropTypes.bool,
  readOnly: PropTypes.bool,
  info: PropTypes.string,
  errors: PropTypes.array,
  children: PropTypes.node
}

export function ToggleField({
  label,
  readOnly,
  disabled,
  onChange,
  checked,
  ...props
}) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
      <Typography>{label}</Typography>
      <Box sx={{ flex: 1 }} />
      <Toggle
        disabled={disabled}
        onChange={onChange}
        defaultChecked={checked}
        readOnly={readOnly}
        {...props}
      />
    </Box>
  )
}

ToggleField.propTypes = {
  label: PropTypes.string,
  info: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  checked: PropTypes.bool
}

export function TextField({
  label,
  errors,
  info,
  readOnly,
  ...props
}) {
  return (
    <InputField
      label={label}
      info={info}
      errors={errors}
      readOnly={readOnly}
    >
      <InputFieldFrame>
        <InputFieldText readOnly={readOnly} {...props} />
      </InputFieldFrame>  
    </InputField>
  )
}

TextField.propTypes = {
  label: PropTypes.string,
  loading: PropTypes.bool,
  errors: PropTypes.array,
  info: PropTypes.string,
  readOnly: PropTypes.bool
}

export function ColorField({
  label,
  defaultValue,
  onChange,
  name,
  info,
  children,
  ...props
}) {
  const [color, setColor] = useState(defaultValue)
  useEffect(() => {
    setColor(defaultValue)
  }, [defaultValue])
  const handleSetColor = (value) => {
    setColor(value)
    onChange?.({
      target: {
        name,
        value
      }
    })
  }
  return (
    <InputField
      label={label}
      info={info}
      {...props}
    >
      <InputFieldFrame sx={{ border: 'none', gap: 1, flexDirection: 'row', alignItems: 'flex-start' }}>
        <HexColorPicker color={color} style={{ borderRadius: 0 }} onChange={handleSetColor} />
        {children}
      </InputFieldFrame>
    </InputField>
  )
}

ColorField.propTypes = {
  label: PropTypes.string,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  name: PropTypes.string,
  info: PropTypes.string,
  children: PropTypes.node
}

export default InputField;
